import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { CheckinState, reportEvent } from '../../../../api/stay-checkin-client';
import { spacing } from '../../../../style-utils/theme';
import { StatusLabel } from '../../../../motion';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { FormModal } from '../../../modal/form-modal';
import { ErrorText } from '../../../onboarding/onboarding.styled';
import { Actions, Body, Primary, Secondary } from '../../../onboarding/stay/public-stay-page.styled';
import { formatStayDate, StayFacts } from '../../../onboarding/stay/stay-steps';
import { t } from '../../../onboarding/stay/stay-strings';
import { DeskShareStep } from './desk-share-step';
import { ActionRow, Note, Reason, StepHeading } from './guest-checkin.styled';

const L = 'en' as const;
const HELP_MAX = 280;

const GuideSlot = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${spacing.xs} 0 0;
`;

/** S3's Primary look on a router link (the token stays in the path it came in). */
const GuideLink = styled(Primary.withComponent(Link))`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`;

/**
 * STEP 4 — PAYMENT (S10's slot). Shown only when the server says a payment is
 * required; in S6 nothing can be paid yet.
 */
export const PaymentStep: React.FC = () => (
  <>
    <StepHeading>{t(L, 'paymentTitle')}</StepHeading>
    <Body role="status">{t(L, 'paymentSoon')}</Body>
  </>
);

/**
 * DONE (plan S6, D-step6 + K-25): "Check-in complete", the place S7's arrival
 * guide takes, and the guest's own reports to the host — I've arrived / I've
 * checked out (from the check-in date; before it the reason is shown) and I
 * need help (a short message; the host is told, the message is never in the
 * notification).
 */
export const DoneStep: React.FC<{
  token: string;
  state: CheckinState;
  onReported: (kind: 'arrived' | 'checked_out' | 'help') => void;
  onError: (err: unknown) => string | null;
}> = ({ token, state, onReported, onError }) => {
  const [busy, setBusy] = useState<'arrived' | 'checked_out' | 'help' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [note, setNote] = useState('');
  const open = state.stay.building_today >= state.stay.check_in;
  const availableOn = t(L, 'availableOn', { date: formatStayDate(state.stay.check_in) });

  const send = async (kind: 'arrived' | 'checked_out' | 'help') => {
    if (busy) return;
    setBusy(kind);
    setProblem(null);
    try {
      await reportEvent(token, kind, kind === 'help' ? note.trim() || undefined : undefined);
      setNotice(kind === 'arrived' ? t(L, 'arrivedDone') : kind === 'checked_out' ? t(L, 'checkedOutDone') : t(L, 'hostTold'));
      if (kind === 'help') {
        setHelpOpen(false);
        setNote('');
      }
      onReported(kind);
    } catch (err) {
      setProblem(onError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <StepHeading>{t(L, 'doneCompleteTitle')}</StepHeading>
      <Body>{t(L, 'doneLookForward', { first: state.guest_first_name })}</Body>
      <StayFacts
        stay={{
          check_in: state.stay.check_in,
          check_out: state.stay.check_out,
          nights: state.stay.nights,
          check_in_time: state.listing.check_in_time,
          check_out_time: state.listing.check_out_time,
        }}
        zone={state.listing.timezone}
      />
      {/* The done-screen slot: S7's stay guide (/s/:token/guide). No redirect
          from /s/:token — the guest's acts use this screen. */}
      <GuideSlot data-arrival-slot="">
        <GuideLink to={`/s/${token}/guide`}>{t(L, 'guide.openGuide')}</GuideLink>
      </GuideSlot>
      {/* IS7: a link approved after check-in was finished asks here; a finished check-in
          is never undone. Whoever agreed can stop sharing here too. */}
      {state.desk_share ? (
        <DeskShareStep
          token={token}
          share={state.desk_share}
          compact
          onSaved={() => onReported('arrived') /* the page ignores the kind: it re-reads the state */}
          onError={onError}
        />
      ) : null}
      {notice ? <Note role="status">{notice}</Note> : null}
      {problem && !helpOpen ? <ErrorText>{problem}</ErrorText> : null}
      <Actions>
        <ActionRow>
          <Secondary
            type="button"
            onClick={() => send('arrived')}
            disabled={!open || !!state.events.arrived_at}
            aria-describedby={!open ? 'done-available-on' : undefined}
            aria-busy={busy === 'arrived' || undefined}
          >
            <StatusLabel state={busy === 'arrived' ? 'busy' : 'idle'} labels={{ idle: t(L, 'arrivedAction'), busy: t(L, 'sending') }} />
          </Secondary>
          <Secondary
            type="button"
            onClick={() => send('checked_out')}
            disabled={!open || !!state.events.checked_out_at}
            aria-describedby={!open ? 'done-available-on' : undefined}
            aria-busy={busy === 'checked_out' || undefined}
          >
            <StatusLabel
              state={busy === 'checked_out' ? 'busy' : 'idle'}
              labels={{ idle: t(L, 'checkedOutAction'), busy: t(L, 'sending') }}
            />
          </Secondary>
          {!open ? <Reason id="done-available-on">{availableOn}</Reason> : null}
        </ActionRow>
        <Primary
          type="button"
          onClick={() => {
            setProblem(null);
            setHelpOpen(true);
          }}
        >
          {t(L, 'helpAction')}
        </Primary>
      </Actions>
      {helpOpen ? (
        <FormModal
          title={t(L, 'helpTitle')}
          open
          onClose={() => setHelpOpen(false)}
          isDirty={!!note.trim()}
          saving={busy === 'help'}
          footer={
            <Primary type="button" onClick={() => send('help')} aria-busy={busy === 'help' || undefined}>
              <StatusLabel state={busy === 'help' ? 'busy' : 'idle'} labels={{ idle: t(L, 'sendToHost'), busy: t(L, 'sending') }} />
            </Primary>
          }
        >
          {/* A refused request (HELP_LIMIT, a lost link) is said inside the open sheet, not behind it. */}
          {problem ? <ErrorText>{problem}</ErrorText> : null}
          <CountedTextarea
            id="help-note"
            label={t(L, 'helpLabel')}
            value={note}
            max={HELP_MAX}
            onChange={e => setNote(e.target.value.slice(0, HELP_MAX))}
          />
        </FormModal>
      ) : null}
    </>
  );
};

/** After check-out, inside the link's grace days (K-10). */
export const EndedView: React.FC<{ first: string; listing: string }> = ({ first, listing }) => (
  <>
    <StepHeading as="h1">{t(L, 'endedTitle', { first })}</StepHeading>
    <Body>{t(L, 'endedBody', { listing })}</Body>
  </>
);
